import {
  NumistaCategory,
  NumistaOrder,
  type Meta,
  type NumistaPreview,
  type NumistaSearchInput,
  type NumistaSearchItem,
  type NumistaTarget,
} from '@numistic/contracts'
import {
  useMeta,
  useNumistaList,
  useNumistaPreview,
  useNumistaSearch,
  useNumistaStatus,
  useSession,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Field,
  Input,
  Select,
} from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { LanguageToggle, withLanguage, type TextLanguage } from '../../catalog/language-toggle.js'
import {
  BulkImportBar,
  CollectionPicker,
  OutcomeLinks,
  useImportToCollection,
  type ImportOutcome,
} from '../../catalog/numista-bulk.js'
import { useErrorText } from '../../lib/errors.js'
import { monthName } from '../../lib/format.js'
import { requireSession } from '../../lib/guards.js'
import { MetaView } from '../../meta/meta-view.js'
import { RefCombobox } from '../../meta/widgets/ref-combobox.js'
import { useRefOptions } from '../../meta/widgets/ref-options.js'
import { rootRoute } from '../root.js'

const PAGE_SIZE = 20

export const numistaImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog/import/numista',
  validateSearch: z.object({
    q: z.coerce.string().optional().catch(undefined),
    page: z.coerce.number().int().min(1).optional().catch(undefined),
    n: z.coerce.number().int().positive().optional().catch(undefined),
    // Фильтры Numista API (GET /types).
    issuer: z.string().optional().catch(undefined),
    category: NumistaCategory.optional().catch(undefined),
    catalogue: z.coerce.number().int().positive().optional().catch(undefined),
    number: z.coerce.string().optional().catch(undefined),
    date: z.coerce.string().optional().catch(undefined),
    year: z.coerce.string().optional().catch(undefined),
    size: z.coerce.string().optional().catch(undefined),
    weight: z.coerce.string().optional().catch(undefined),
    order: NumistaOrder.optional().catch(undefined),
  }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: NumistaImportPage,
})

type Filters = Omit<NumistaSearchInput, 'q' | 'page'>
/** Каталоги, по которым ищут чаще всего: Krause, Pick, Yeoman, Schön, Friedberg. */
const POPULAR_CATALOGUES = ['KM', 'P', 'Y', 'N', 'Schön', 'Fr', 'C', 'X']
const FILTER_KEYS = [
  'issuer',
  'category',
  'catalogue',
  'number',
  'date',
  'year',
  'size',
  'weight',
  'order',
] as const

/** Запрос к Numista из адреса; null — искать ещё нечего (API нужен текст или фильтр). */
function searchInput(s: Record<string, unknown>): NumistaSearchInput | null {
  const input: Record<string, unknown> = {}
  for (const key of ['q', 'page', ...FILTER_KEYS])
    if (s[key] !== undefined && s[key] !== '') input[key] = s[key]
  const ready =
    (typeof input.q === 'string' && input.q.length >= 2) ||
    input.issuer ||
    input.catalogue ||
    input.date ||
    input.year
  return ready ? (input as NumistaSearchInput) : null
}

function NumistaImportPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const search = numistaImportRoute.useSearch()
  const navigate = useNavigate({ from: '/catalog/import/numista' })
  const status = useNumistaStatus()
  const meta = useMeta().data
  const page = search.page ?? 1
  // Поиск тратит квоту Numista — только по кнопке, не по мере ввода.
  const query = useMemo(() => searchInput(search), [search])
  const results = useNumistaSearch(query ? { ...query, page } : null)
  const pages = results.data ? Math.ceil(results.data.count / PAGE_SIZE) : 0
  const { data: session } = useSession()
  const editor = session?.user.role === 'admin' || session?.user.role === 'editor'
  // Отмеченные для массового импорта; выбор сохраняется при листании страниц.
  const [picked, setPicked] = useState(() => new Map<number, string>())
  const togglePick = (item: NumistaSearchItem, on: boolean) =>
    setPicked((prev) => {
      const next = new Map(prev)
      if (on) next.set(item.numistaId, item.title)
      else next.delete(item.numistaId)
      return next
    })
  const openPreview = (numistaId: number) =>
    void navigate({ search: (s) => ({ ...s, n: numistaId }), resetScroll: false })

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold">{t('numista.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('numista.hint')}</p>
        </div>
        {status.data ? (
          <p className="text-xs text-muted-foreground">
            {t('numista.quota', {
              used: status.data.used,
              quota: status.data.quota,
              month: monthName(status.data.month),
            })}
          </p>
        ) : null}
      </div>
      {status.data && !status.data.configured ? (
        <Alert variant="destructive">{t('numista.notConfigured')}</Alert>
      ) : null}

      <SearchForm
        // Листание страниц не сбрасывает черновик фильтров — только смена самого поиска.
        key={JSON.stringify({ ...query, page: undefined })}
        initial={search}
        busy={results.isFetching}
        onSearch={(next) => void navigate({ search: next })}
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* Список результатов остаётся на месте, пока листается длинный предпросмотр. */}
        <section className="grid content-start gap-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1">
          {results.error ? (
            <Alert variant="destructive">{errorText.request(results.error)}</Alert>
          ) : null}
          {!query ? (
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              {t('numista.startHint')}
            </p>
          ) : results.isFetching && !results.data ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : null}
          {results.data ? (
            <>
              <p className="text-sm text-muted-foreground">
                {results.data.count
                  ? t('numista.found', { count: results.data.count })
                  : t('numista.nothing')}
              </p>
              <BulkImportBar
                selected={picked}
                editor={editor}
                onClear={() => setPicked(new Map())}
                onDone={(id) =>
                  setPicked((prev) => {
                    const next = new Map(prev)
                    next.delete(id)
                    return next
                  })
                }
                onOpen={openPreview}
              />
              <ul className="grid gap-2" aria-label={t('numista.title')}>
                {results.data.items.map((item) => (
                  <li key={item.numistaId} className="flex items-start gap-2">
                    <Checkbox
                      aria-label={t('numista.bulk.pick', { title: item.title })}
                      checked={picked.has(item.numistaId)}
                      onChange={(e) => togglePick(item, e.target.checked)}
                      className="mt-3 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <ResultRow
                        item={item}
                        selected={item.numistaId === search.n}
                        // Прокрутку к предпросмотру делает сам Preview — роутер её не сбрасывает.
                        onSelect={() => openPreview(item.numistaId)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
              {pages > 1 ? (
                <div className="flex items-center justify-between text-sm">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => void navigate({ search: (s) => ({ ...s, page: page - 1 }) })}
                  >
                    {t('numista.prev')}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {t('numista.page', { page, pages })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= pages}
                    onClick={() => void navigate({ search: (s) => ({ ...s, page: page + 1 }) })}
                  >
                    {t('numista.next')}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
        {search.n && meta ? (
          <Preview key={search.n} numistaId={search.n} meta={meta} />
        ) : query && results.data?.count ? (
          <p className="hidden rounded-lg border border-dashed p-6 text-sm text-muted-foreground lg:block">
            {t('numista.pickHint')}
          </p>
        ) : null}
      </div>
    </div>
  )
}

/** Миниатюра Numista: если не загрузилась — пустая плашка вместо значка битой картинки. */
function Thumb(props: React.ImgHTMLAttributes<HTMLImageElement> & { src: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <span className={cn(props.className, 'block bg-muted')} aria-hidden />
  return <img {...props} onError={() => setFailed(true)} />
}

/** «1990», «1990-2000» из полей «с» и «по». */
function toRange(from: string, to: string): string | undefined {
  const a = from.trim().replace(',', '.')
  const b = to.trim().replace(',', '.')
  if (a && b) return a === b ? a : `${a}-${b}`
  return a || b || undefined
}
function fromRange(range: string | undefined): [string, string] {
  // Годы бывают отрицательными (до н. э.): «-300», «-300--250», «-50-20».
  const m = range?.match(/^(-?[\d.]+)(?:-(-?[\d.]+))?$/)
  return m ? [m[1]!, m[2] ?? ''] : ['', '']
}

/**
 * Строка поиска и фильтры Numista API: категория, эмитент, каталог и номер, годы выпуска,
 * год на предмете, размер, вес, сортировка. Черновик — локально, в адрес уходит по «Найти».
 */
function SearchForm({
  initial,
  busy,
  onSearch,
}: {
  initial: Record<string, unknown>
  busy: boolean
  onSearch: (search: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const str = (k: string) => (typeof initial[k] === 'string' ? (initial[k] as string) : '')
  const [text, setText] = useState(str('q'))
  const [category, setCategory] = useState(str('category'))
  const [issuer, setIssuer] = useState<string | null>(str('issuer') || null)
  const [catalogue, setCatalogue] = useState(initial.catalogue ? String(initial.catalogue) : '')
  const [number, setNumber] = useState(str('number'))
  const [dateFrom, dateTo] = fromRange(str('date'))
  const [dFrom, setDFrom] = useState(dateFrom)
  const [dTo, setDTo] = useState(dateTo)
  const [year, setYear] = useState(str('year'))
  const [sizeFrom, sizeTo] = fromRange(str('size'))
  const [sFrom, setSFrom] = useState(sizeFrom)
  const [sTo, setSTo] = useState(sizeTo)
  const [weightFrom, weightTo] = fromRange(str('weight'))
  const [wFrom, setWFrom] = useState(weightFrom)
  const [wTo, setWTo] = useState(weightTo)
  const [order, setOrder] = useState(str('order'))
  const active = FILTER_KEYS.filter((k) => initial[k] !== undefined && initial[k] !== '').length
  const [open, setOpen] = useState(active > 0)
  const issuers = useNumistaList('issuers', open)
  const catalogues = useNumistaList('catalogues', open)
  const issuerOptions = useMemo(
    () =>
      (issuers.data ?? []).map((i) => ({
        id: i.code,
        label: i.parent && i.parent !== i.name ? `${i.name} (${i.parent})` : i.name,
      })),
    [issuers.data],
  )
  const catalogueOptions = useMemo(() => {
    // Самые ходовые каталоги — первыми, остальные по коду.
    const rank = (code: string) => {
      const i = POPULAR_CATALOGUES.indexOf(code)
      return i < 0 ? POPULAR_CATALOGUES.length : i
    }
    return [...(catalogues.data ?? [])]
      .sort((a, b) => rank(a.code) - rank(b.code) || a.code.localeCompare(b.code, 'en'))
      .map((c) => ({ id: String(c.id), label: `${c.code} — ${c.title}` }))
  }, [catalogues.data])
  const ids = { issuer: useId(), catalogue: useId() }
  const filters: Filters = {
    category: (category || undefined) as Filters['category'],
    issuer: issuer ?? undefined,
    catalogue: catalogue ? Number(catalogue) : undefined,
    number: catalogue && number.trim() ? number.trim() : undefined,
    date: toRange(dFrom, dTo),
    year: year.trim() || undefined,
    size: toRange(sFrom, sTo),
    weight: toRange(wFrom, wTo),
    order: (order || undefined) as Filters['order'],
  }
  const q = text.trim()
  const canSearch =
    q.length >= 2 || Boolean(filters.issuer || filters.catalogue || filters.date || filters.year)
  const submit = () => {
    if (!canSearch) return
    onSearch(
      Object.fromEntries(
        Object.entries({ q: q || undefined, ...filters }).filter(([, v]) => v !== undefined),
      ),
    )
  }
  const reset = () => {
    setCategory('')
    setIssuer(null)
    setCatalogue('')
    setNumber('')
    setDFrom('')
    setDTo('')
    setYear('')
    setSFrom('')
    setSTo('')
    setWFrom('')
    setWTo('')
    setOrder('')
  }
  const range = (
    key: string,
    label: string,
    a: string,
    setA: (v: string) => void,
    b: string,
    setB: (v: string) => void,
  ) => (
    <div
      role="group"
      aria-labelledby={`${ids.issuer}-${key}`}
      className="grid content-start gap-1.5"
    >
      <span id={`${ids.issuer}-${key}`} className="text-sm leading-none font-medium">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <Input
          inputMode="decimal"
          aria-label={`${label} — ${t('numista.filters.from')}`}
          placeholder={t('numista.filters.from')}
          value={a}
          onChange={(e) => setA(e.target.value)}
        />
        <span aria-hidden className="text-muted-foreground">
          —
        </span>
        <Input
          inputMode="decimal"
          aria-label={`${label} — ${t('numista.filters.to')}`}
          placeholder={t('numista.filters.to')}
          value={b}
          onChange={(e) => setB(e.target.value)}
        />
      </div>
    </div>
  )

  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <div className="flex gap-2">
        <Input
          type="search"
          aria-label={t('common.search')}
          placeholder={t('numista.searchPlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {t('numista.filters.toggle')}
          {active ? (
            <Badge variant="muted" className="ml-1.5">
              {active}
            </Badge>
          ) : null}
        </Button>
        <Button type="submit" disabled={busy || !canSearch}>
          {t('numista.search')}
        </Button>
      </div>
      {open ? (
        <div className="grid items-start gap-x-4 gap-y-5 rounded-lg border bg-card p-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t('numista.filters.category')}>
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('numista.filters.any')}</option>
              {NumistaCategory.options.map((c) => (
                <option key={c} value={c}>
                  {t(`numista.filters.categories.${c}`)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid content-start gap-1.5">
            <label htmlFor={ids.issuer} className="text-sm leading-none font-medium">
              {t('numista.filters.issuer')}
            </label>
            <RefCombobox
              id={ids.issuer}
              options={issuerOptions}
              value={issuer}
              onChange={setIssuer}
            />
            {issuers.isPending ? (
              <span className="text-xs text-muted-foreground">{t('common.loading')}</span>
            ) : null}
          </div>
          <div className="grid content-start gap-1.5">
            <label htmlFor={ids.catalogue} className="text-sm leading-none font-medium">
              {t('numista.filters.catalogue')}
            </label>
            <div className="flex gap-2">
              <div className="min-w-0 flex-1">
                <RefCombobox
                  id={ids.catalogue}
                  options={catalogueOptions}
                  value={catalogue || null}
                  onChange={(v) => setCatalogue(v ?? '')}
                />
              </div>
              <Input
                aria-label={t('numista.filters.number')}
                placeholder={t('numista.filters.number')}
                disabled={!catalogue}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                className="w-28"
              />
            </div>
          </div>
          <Field label={t('numista.filters.order')}>
            <Select value={order} onChange={(e) => setOrder(e.target.value)}>
              <option value="">{t('numista.filters.orderAuto')}</option>
              {NumistaOrder.options.map((o) => (
                <option key={o} value={o}>
                  {t(`numista.filters.orders.${o}`)}
                </option>
              ))}
            </Select>
          </Field>
          {range('date', t('numista.filters.date'), dFrom, setDFrom, dTo, setDTo)}
          <Field label={t('numista.filters.year')}>
            <Input
              value={year}
              placeholder={t('numista.filters.yearPlaceholder')}
              title={t('numista.filters.yearHint')}
              onChange={(e) => setYear(e.target.value)}
            />
          </Field>
          {range('size', t('numista.filters.size'), sFrom, setSFrom, sTo, setSTo)}
          {range('weight', t('numista.filters.weight'), wFrom, setWFrom, wTo, setWTo)}
          <div className="flex flex-wrap items-center gap-2 border-t pt-4 sm:col-span-2 lg:col-span-3">
            <Button type="submit" disabled={busy || !canSearch}>
              {t('numista.filters.apply')}
            </Button>
            <Button type="button" variant="ghost" onClick={reset}>
              {t('numista.filters.reset')}
            </Button>
            <p className="text-xs text-muted-foreground">{t('numista.filters.hint')}</p>
          </div>
        </div>
      ) : null}
    </form>
  )
}

function years(item: { minYear: number | null; maxYear: number | null }) {
  if (item.minYear === null) return null
  return item.maxYear !== null && item.maxYear !== item.minYear
    ? `${item.minYear}–${item.maxYear}`
    : String(item.minYear)
}

function ResultRow({
  item,
  selected,
  onSelect,
}: {
  item: NumistaSearchItem
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected || undefined}
      className="flex w-full items-start gap-3 rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary aria-[current=true]:border-primary aria-[current=true]:ring-2 aria-[current=true]:ring-primary/30"
    >
      <div className="size-16 shrink-0 overflow-hidden rounded bg-muted">
        {item.thumbnail ? (
          <Thumb src={item.thumbnail} alt="" loading="lazy" className="size-full object-contain" />
        ) : null}
      </div>
      <span className="grid min-w-0 gap-1">
        <span className="line-clamp-2 text-sm font-medium leading-snug">{item.title}</span>
        <span className="text-xs text-muted-foreground">
          {[item.issuer, years(item), `N# ${item.numistaId}`].filter(Boolean).join(' · ')}
        </span>
        {item.existing.catalog || item.existing.private ? (
          <span className="flex flex-wrap gap-1">
            {item.existing.catalog ? <Badge variant="muted">{t('numista.inCatalog')}</Badge> : null}
            {item.existing.private ? <Badge variant="muted">{t('numista.inMine')}</Badge> : null}
          </span>
        ) : null}
      </span>
    </button>
  )
}

function Preview({ numistaId, meta }: { numistaId: number; meta: Meta }) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const preview = useNumistaPreview(numistaId)
  const { data: session } = useSession()
  const editor = session?.user.role === 'admin' || session?.user.role === 'editor'
  const importOne = useImportToCollection()
  const [pending, setPending] = useState(false)
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null)
  const [toCollection, setToCollection] = useState(false)
  const [collectionId, setCollectionId] = useState('')
  const issuerFieldId = useId()
  const { options: issuers } = useRefOptions('issuers', meta)
  const [typeOverride, setTypeCode] = useState<string>()
  const [issuerId, setIssuerId] = useState<string | null>(null)
  // Что видно — то и импортируется: русский перевод или оригинал Numista.
  const [language, setLanguage] = useState<TextLanguage>('ru')
  const top = useRef<HTMLDivElement>(null)
  const data = preview.data
  const typeCode = typeOverride ?? data?.typeCode

  // На телефоне предпросмотр под списком — когда он загрузился, прокручиваем к нему
  // (пока идёт загрузка, страница короче экрана и прокручивать некуда).
  const loaded = Boolean(data)
  useEffect(() => {
    if (loaded && window.matchMedia?.('(max-width: 1023px)').matches)
      top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [loaded])

  if (preview.error) return <Alert variant="destructive">{errorText.request(preview.error)}</Alert>
  if (!data || !typeCode)
    return (
      <div ref={top} className="rounded-lg border p-6 text-sm text-muted-foreground">
        {t('numista.loadingPreview')}
      </div>
    )

  const translated = Object.keys(data.originals).length > 0
  const values = withLanguage(
    issuerId ? { ...data.values, issuer: issuerId } : data.values,
    data.originals,
    language,
  )
  const unmatchedIssuer = data.unmatched.find((u) => u.field === 'issuer')
  const fieldName = (code: string) => meta.fields.find((f) => f.code === code)?.name.ru ?? code
  const issuerMissing = outcome?.kind === 'issuer' && !issuerId
  // Импорт не уводит со страницы: поиск остаётся, запись и экземпляр — ссылками в новой вкладке.
  const submit = async (target: NumistaTarget) => {
    setPending(true)
    setOutcome(null)
    try {
      setOutcome(
        await importOne({
          numistaId,
          target,
          typeCode,
          issuerId: issuerId ?? undefined,
          translate: language === 'ru',
          collectionId: toCollection ? collectionId : undefined,
        }),
      )
    } finally {
      setPending(false)
    }
  }
  const imageCaption = (img: NumistaPreview['images'][number]) =>
    img.side === 'watermark' ? t('numista.watermark') : t(`catalog.images.roles.${img.role}`)
  const groups = [
    { title: t('numista.willCreate'), items: data.unmatched.filter((u) => u.create) },
    {
      title: t('numista.unmatched'),
      items: data.unmatched.filter((u) => !u.create && u.field !== 'issuer'),
    },
  ]

  return (
    <div ref={top} className="scroll-mt-16">
      <Card>
        <CardHeader className="gap-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="grid min-w-0 gap-1">
              <CardTitle className="text-xl leading-snug">{String(values.title ?? '')}</CardTitle>
              <p className="text-xs text-muted-foreground">
                N# {data.numistaId}
                {data.url ? (
                  <>
                    {' · '}
                    <a
                      href={data.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline underline-offset-4 hover:text-foreground"
                    >
                      {t('numista.openOnNumista')}
                    </a>
                  </>
                ) : null}
              </p>
            </div>
            {translated ? <LanguageToggle value={language} onChange={setLanguage} /> : null}
          </div>
          {data.translation === 'partial' ? (
            <p className="text-xs text-muted-foreground">{t('translation.partial')}</p>
          ) : translated ? (
            <p className="text-xs text-muted-foreground">
              {language === 'ru' ? t('translation.importRu') : t('translation.importOriginal')}
            </p>
          ) : null}
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('numista.type')}>
              <Select value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
                {meta.itemTypes
                  .filter((x) => x.active !== false)
                  .map((x) => (
                    <option key={x.code} value={x.code}>
                      {x.name.ru}
                    </option>
                  ))}
              </Select>
            </Field>
            {unmatchedIssuer ? (
              <div className="grid content-start gap-1.5">
                <label htmlFor={issuerFieldId} className="text-sm font-medium">
                  {t('numista.issuer')}
                </label>
                <RefCombobox
                  id={issuerFieldId}
                  options={issuers}
                  value={issuerId}
                  onChange={setIssuerId}
                  invalid={issuerMissing}
                />
                <p className="text-xs text-muted-foreground">
                  {t('numista.issuerHint', { name: unmatchedIssuer.value })}
                </p>
              </div>
            ) : null}
          </div>

          {data.images.some((img) => img.thumbnail) ? (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label={t('numista.images')}>
              {data.images.map((img) =>
                img.thumbnail ? (
                  <li key={img.role} className="grid gap-1">
                    <Thumb
                      src={img.thumbnail}
                      alt={imageCaption(img)}
                      className="aspect-square w-full rounded-md border bg-muted object-contain"
                    />
                    <span className="text-center text-xs text-muted-foreground">
                      {imageCaption(img)}
                    </span>
                  </li>
                ) : null,
              )}
            </ul>
          ) : null}

          <MetaView typeCode={typeCode} level="catalog" values={values} hide={['title']} grouped />

          <Variants preview={data} />

          {groups.map((group) =>
            group.items.length ? (
              <div key={group.title} className="grid gap-1.5 text-sm">
                <h3 className="font-medium">{group.title}</h3>
                <dl className="grid gap-x-6 gap-y-1 text-muted-foreground sm:grid-cols-[minmax(10rem,auto)_1fr]">
                  {groupByField(group.items).map(([field, list]) => (
                    <div key={field} className="contents">
                      <dt>{fieldName(field)}</dt>
                      <dd className="text-foreground/90">{list.join(', ')}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null,
          )}
        </CardContent>
        {/* На широком экране действия всегда под рукой, как бы длинно ни было описание. */}
        <div className="grid gap-2 rounded-b-lg border-t bg-card/95 px-6 py-4 backdrop-blur lg:sticky lg:bottom-0">
          {outcome?.kind === 'error' ? (
            <Alert variant="destructive">{outcome.message}</Alert>
          ) : outcome?.kind === 'issuer' ? (
            <Alert variant="destructive">{t('numista.issuerRequired')}</Alert>
          ) : outcome?.kind === 'done' ? (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" role="status">
              <span className="font-medium">
                ✓ {outcome.specimenId ? t('numista.doneWithSpecimen') : t('numista.done')}
              </span>
              <OutcomeLinks outcome={outcome} />
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={toCollection}
                disabled={pending}
                onChange={(e) => setToCollection(e.target.checked)}
              />
              {t('numista.bulk.addToCollection')}
            </label>
            {toCollection ? (
              <CollectionPicker
                label={t('numista.bulk.collection')}
                value={collectionId}
                onChange={setCollectionId}
              />
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {editor ? (
              <Button disabled={pending} onClick={() => void submit('catalog')}>
                {data.existing.catalog ? t('numista.updateCatalog') : t('numista.importCatalog')}
              </Button>
            ) : null}
            <Button
              variant={editor ? 'outline' : 'default'}
              disabled={pending}
              onClick={() => void submit('private')}
            >
              {data.existing.private ? t('numista.updatePrivate') : t('numista.importPrivate')}
            </Button>
            {[data.existing.catalog, data.existing.private].map((id) =>
              id ? (
                <Link
                  key={id}
                  to="/catalog/$id"
                  params={{ id }}
                  target="_blank"
                  className="text-sm text-primary underline underline-offset-4 hover:decoration-2"
                >
                  {t('numista.openExisting')}:{' '}
                  {id === data.existing.catalog ? t('numista.inCatalog') : t('numista.inMine')} ↗
                </Link>
              ) : null,
            )}
          </div>
          {editor ? (
            <p className="text-xs text-muted-foreground">{t('numista.catalogNote')}</p>
          ) : null}
        </div>
      </Card>
    </div>
  )
}

/** «Каталожные номера: TBB# 205, TBB# 201a» вместо строки на каждый номер. */
function groupByField(items: Array<{ field: string; value: string }>): Array<[string, string[]]> {
  const out = new Map<string, string[]>()
  for (const u of items) out.set(u.field, [...(out.get(u.field) ?? []), u.value])
  return [...out]
}

function Variants({ preview }: { preview: NumistaPreview }) {
  const { t } = useTranslation()
  if (!preview.variants.length) return null
  return (
    <div className="grid gap-1.5 text-sm">
      <h3 className="font-medium">{t('numista.variants')}</h3>
      <ul className="flex flex-wrap gap-1.5">
        {preview.variants.map((v) => (
          <li key={v.numistaIssueId} className="rounded border px-2 py-0.5 text-xs">
            {v.label}
            {typeof v.values.mintage === 'number'
              ? ` · ${v.values.mintage.toLocaleString('ru-RU')}`
              : ''}
          </li>
        ))}
      </ul>
    </div>
  )
}
